// src/components/ChatWindow.tsx

import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Paper, 
    Avatar, 
    IconButton, 
    Button, 
    Stack, 
    CircularProgress, 
    TextField 
} from '@mui/material';
import { 
    ThumbUpOutlined as ThumbUpOutlinedIcon, 
    ThumbDownOutlined as ThumbDownOutlinedIcon,
    ContentCopyOutlined as ContentCopyOutlinedIcon,
    Autorenew as AutorenewIcon,
    ThumbUp as ThumbUpIcon,
    ThumbDown as ThumbDownIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown'; 

export interface Message {
  id: string;
  sender: 'user' | 'ai';
  text: string;
  agentName?: string; 
  isTyping?: boolean; // <-- Added flag to trigger typing effect
}

interface ChatWindowProps {
  messages: Message[];
  isThinking?: boolean; // <-- Added flag to show 5-7s delay animation
}

// --- SUB-COMPONENT FOR TYPEWRITER EFFECT ---
const TypewriterMarkdown: React.FC<{ text: string, isTyping?: boolean }> = ({ text, isTyping }) => {
    const [displayedText, setDisplayedText] = useState(isTyping ? '' : text);

    useEffect(() => {
        if (!isTyping) {
            setDisplayedText(text);
            return;
        }

        let index = 0;
        const typingSpeed = 15; // ms per character - tweak for faster/slower typing

        const interval = setInterval(() => {
            index += 2; // Output 2 chars at a time for smoother markdown rendering
            if (index >= text.length) {
                setDisplayedText(text);
                clearInterval(interval);
            } else {
                setDisplayedText(text.slice(0, index));
            }
        }, typingSpeed);

        return () => clearInterval(interval);
    }, [text, isTyping]);

    return <ReactMarkdown>{displayedText}</ReactMarkdown>;
};

// Interface for keeping track of message feedback
interface FeedbackState {
    type: 'like' | 'dislike' | null;
    text: string;
    isOpen: boolean;
    submitted: boolean;
}

const ChatWindow: React.FC<ChatWindowProps> = ({ messages, isThinking }) => {
  // State to track feedback per message ID
  const [feedbackMap, setFeedbackMap] = useState<Record<string, FeedbackState>>({});

  const handleFeedbackClick = (msgId: string, type: 'like' | 'dislike') => {
      setFeedbackMap(prev => {
          const currentFeedback = prev[msgId];
          const isSameType = currentFeedback?.type === type;
          
          return {
              ...prev,
              [msgId]: {
                  ...currentFeedback,
                  type: isSameType && currentFeedback?.isOpen ? null : type, // Clear type if closing
                  isOpen: isSameType ? !currentFeedback?.isOpen : true, // Toggle if clicking same, otherwise open
                  submitted: false,
                  text: currentFeedback?.text || ''
              }
          };
      });
  };

  const handleFeedbackTextChange = (msgId: string, text: string) => {
      setFeedbackMap(prev => ({
          ...prev,
          [msgId]: {
              ...prev[msgId],
              text
          }
      }));
  };

  const handleFeedbackSubmit = (msgId: string) => {
       setFeedbackMap(prev => ({
          ...prev,
          [msgId]: {
              ...prev[msgId],
              isOpen: false,
              submitted: true
          }
      }));
  };

  if (messages.length === 0 && !isThinking) {
    return (
      <Box
        sx={{
          flexGrow: 1, 
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          p: 2,
          textAlign: 'center', 
          backgroundImage: "url('/assets/agentverse-background.svg')",
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
        }}
      >
        <Typography
          variant="h3"
          sx={{
            fontFamily: "Gilroy-Bold",
            color: "#374151",
            zIndex: 1,
            fontSize: { xs: '2rem', sm: '2.5rem' },
          }}
        >
          Good Day! How may I <Box component="span" sx={{ background: 'linear-gradient(90deg, #FF40E2 16%, rgba(0, 100, 255, 0.4) 65%, #0064FF 100%)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent', 
            }}>assist</Box> you today
        </Typography>
        
        <Typography
          variant="body1"
          sx={{ fontFamily: "Gilroy-Regular", color: "#6B7280", mt: 1.5, zIndex: 1 }}
        >
          The right AI specialist for every task, just a chat away.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flexGrow: 1, 
        display: 'flex',
        flexDirection: 'column',
        gap: 3.5, 
      }}
    >
      {messages.map((msg, index) => {
        if (msg.sender === 'user') {
          return (
            <Box
              key={msg.id}
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'flex-start',
                gap: 1.5,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: '12px 16px',
                  borderRadius: '12px',
                  bgcolor: '#F3F4F6', 
                  fontFamily: 'Gilroy-Regular',
                  fontSize: '15px',
                  color: '#1F2937',
                  lineHeight: 1.6,
                }}
              >
                {msg.text}
              </Paper>
              <Avatar sx={{ bgcolor: '#1F2937', width: 40, height: 40 }}>U</Avatar>
            </Box>
          );
        }

        if (msg.sender === 'ai') {
          const isLastMessage = index === messages.length - 1;
          const feedback = feedbackMap[msg.id];

          return (
            <Box key={msg.id}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  gap: 1.5,
                }}
              >
                <Avatar
                    sx={{
                        width: 40, 
                        height: 40,
                        background: 'linear-gradient(90deg, rgba(255, 64, 226, 0.3) 0%, rgba(0, 100, 255, 0.1) 42%, rgba(0, 100, 255, 0.3) 100%)',
                    }}
                >
                    <Box
                        component="img"
                        src="/assets/agent-image.svg"
                        alt="Agent Icon"
                        sx={{ width: 24, height: 24 }} 
                    />
                </Avatar>

                <Box sx={{ flex: 1, width: 'calc(100% - 56px)' }}>
                  <Typography sx={{ fontFamily: 'Gilroy-Bold', fontSize: '16px', color: '#1F2937', mb: 1 }}>
                    {msg.agentName || 'Support Agent'}
                  </Typography>

                  <Box
                    sx={{
                      fontFamily: 'Gilroy-Regular',
                      fontSize: '15px',
                      color: '#1F2937',
                      lineHeight: 1.7,
                      '& p': { m: 0, mb: 1.5 },
                      '& ol': { pl: 2.5, m: 0 },
                      '& ul': { pl: 2.5, m: 0 },
                      '& li': { mb: 1 },
                      '& pre': { bgcolor: '#1F2937', color: '#fff', p: 1.5, borderRadius: 2, overflowX: 'auto' },
                      '& code': { fontFamily: 'monospace' }
                    }}
                  >
                    {/* Render message with Typewriter effect */}
                    <TypewriterMarkdown text={msg.text} isTyping={msg.isTyping} />
                  </Box>

                  {/* Feedback Icons Stack */}
                  <Stack direction="row" spacing={0.5} sx={{ mt: 1.5 }}>
                      <IconButton 
                          size="small" 
                          sx={{ color: feedback?.type === 'like' ? '#19AF66' : '#6B7280' }}
                          onClick={() => handleFeedbackClick(msg.id, 'like')}
                      >
                          {feedback?.type === 'like' ? <ThumbUpIcon fontSize="small" /> : <ThumbUpOutlinedIcon fontSize="small" />}
                      </IconButton>
                      <IconButton 
                          size="small" 
                          sx={{ color: feedback?.type === 'dislike' ? '#EF4444' : '#6B7280' }}
                          onClick={() => handleFeedbackClick(msg.id, 'dislike')}
                      >
                          {feedback?.type === 'dislike' ? <ThumbDownIcon fontSize="small" /> : <ThumbDownOutlinedIcon fontSize="small" />}
                      </IconButton>
                      <IconButton size="small" sx={{ color: '#6B7280' }}>
                          <ContentCopyOutlinedIcon fontSize="small" />
                      </IconButton>
                  </Stack>

                  {/* Dynamic Feedback Text Field */}
                  {feedback?.isOpen && !feedback?.submitted && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB', maxWidth: '600px' }}>
                          <Typography sx={{ fontFamily: 'Gilroy-Medium', fontSize: '14px', color: '#374151', mb: 1 }}>
                              {feedback.type === 'like' 
                                  ? 'What did you like about this response? (Optional)' 
                                  : 'What was wrong with this response? (Optional)'}
                          </Typography>
                          <TextField
                              fullWidth
                              multiline
                              rows={2}
                              variant="outlined"
                              placeholder="Provide additional comments..."
                              value={feedback.text || ''}
                              onChange={(e) => handleFeedbackTextChange(msg.id, e.target.value)}
                              sx={{
                                  bgcolor: '#FFFFFF',
                                  '& .MuiOutlinedInput-root': {
                                      fontFamily: 'Gilroy-Regular',
                                      fontSize: '14px',
                                      borderRadius: '8px'
                                  }
                              }}
                          />
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5, gap: 1 }}>
                              <Button
                                  size="small"
                                  onClick={() => handleFeedbackClick(msg.id, feedback.type!)}
                                  sx={{ color: '#6B7280', textTransform: 'none', fontFamily: 'Gilroy-Medium' }}
                              >
                                  Cancel
                              </Button>
                              <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleFeedbackSubmit(msg.id)}
                                  sx={{ 
                                      bgcolor: '#19AF66', 
                                      color: '#fff', 
                                      textTransform: 'none', 
                                      fontFamily: 'Gilroy-Medium', 
                                      boxShadow: 'none',
                                      '&:hover': { bgcolor: '#148C51', boxShadow: 'none' } 
                                  }}
                              >
                                  Submit
                              </Button>
                          </Box>
                      </Box>
                  )}

                  {/* Feedback Submission Confirmation Message */}
                  {feedback?.submitted && (
                      <Typography sx={{ mt: 1.5, fontFamily: 'Gilroy-Medium', fontSize: '13px', color: '#19AF66' }}>
                          Thank you for your feedback!
                      </Typography>
                  )}
                </Box>
              </Box>

              {isLastMessage && !msg.isTyping && !isThinking && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={<AutorenewIcon />}
                        sx={{
                            color: '#101010',
                            borderColor: '#101010', 
                            textTransform: 'none',
                            fontFamily: 'Gilroy-Medium',
                            borderRadius: '30px', 
                            '&:hover': {
                                bgcolor: '#F9FAFB', 
                                borderColor: '#101010', 
                            }
                        }}
                    >
                        Regenerate
                    </Button>
                </Box>
              )}
            </Box>
          );
        }
        return null;
      })}

      {/* --- RENDER THINKING INDICATOR --- */}
      {isThinking && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 1.5 }}>
               <Avatar sx={{ width: 40, height: 40, background: 'linear-gradient(90deg, rgba(255, 64, 226, 0.3) 0%, rgba(0, 100, 255, 0.1) 42%, rgba(0, 100, 255, 0.3) 100%)' }}>
                   <Box component="img" src="/assets/agent-image.svg" alt="Agent Icon" sx={{ width: 24, height: 24 }} />
               </Avatar>
               <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: '#F9FAFB', display: 'flex', alignItems: 'center', gap: 1 }}>
                   <CircularProgress size={16} sx={{ color: '#19AF66' }} />
                   <Typography sx={{ fontFamily: 'Gilroy-Medium', fontSize: '14px', color: '#6B7280' }}>
                       Agent is analyzing your request...
                   </Typography>
               </Box>
          </Box>
      )}
    </Box>
  );
};

export default ChatWindow;


// // src/components/ChatWindow.tsx

// import React, { useState, useEffect } from 'react';
// import { Box, Typography, Paper, Avatar, IconButton, Button, Stack, CircularProgress } from '@mui/material';
// import { 
//     ThumbUpOutlined as ThumbUpOutlinedIcon, 
//     ThumbDownOutlined as ThumbDownOutlinedIcon,
//     ContentCopyOutlined as ContentCopyOutlinedIcon,
//     Autorenew as AutorenewIcon
// } from '@mui/icons-material';
// import ReactMarkdown from 'react-markdown'; 

// export interface Message {
//   id: string;
//   sender: 'user' | 'ai';
//   text: string;
//   agentName?: string; 
//   isTyping?: boolean; // <-- Added flag to trigger typing effect
// }

// interface ChatWindowProps {
//   messages: Message[];
//   isThinking?: boolean; // <-- Added flag to show 5-7s delay animation
// }

// // --- SUB-COMPONENT FOR TYPEWRITER EFFECT ---
// const TypewriterMarkdown: React.FC<{ text: string, isTyping?: boolean }> = ({ text, isTyping }) => {
//     const [displayedText, setDisplayedText] = useState(isTyping ? '' : text);

//     useEffect(() => {
//         if (!isTyping) {
//             setDisplayedText(text);
//             return;
//         }

//         let index = 0;
//         const typingSpeed = 15; // ms per character - tweak for faster/slower typing

//         const interval = setInterval(() => {
//             index += 2; // Output 2 chars at a time for smoother markdown rendering
//             if (index >= text.length) {
//                 setDisplayedText(text);
//                 clearInterval(interval);
//             } else {
//                 setDisplayedText(text.slice(0, index));
//             }
//         }, typingSpeed);

//         return () => clearInterval(interval);
//     }, [text, isTyping]);

//     return <ReactMarkdown>{displayedText}</ReactMarkdown>;
// };

// const ChatWindow: React.FC<ChatWindowProps> = ({ messages, isThinking }) => {

//   if (messages.length === 0 && !isThinking) {
//     return (
//       <Box
//         sx={{
//           flexGrow: 1, 
//           display: 'flex',
//           flexDirection: 'column',
//           justifyContent: 'center',
//           alignItems: 'center',
//           position: 'relative',
//           p: 2,
//           textAlign: 'center', 
//           backgroundImage: "url('/assets/agentverse-background.svg')",
//           backgroundRepeat: 'no-repeat',
//           backgroundPosition: 'center',
//         }}
//       >
//         <Typography
//           variant="h3"
//           sx={{
//             fontFamily: "Gilroy-Bold",
//             color: "#374151",
//             zIndex: 1,
//             fontSize: { xs: '2rem', sm: '2.5rem' },
//           }}
//         >
//           Good Day! How may I <Box component="span" sx={{ background: 'linear-gradient(90deg, #FF40E2 16%, rgba(0, 100, 255, 0.4) 65%, #0064FF 100%)',
//               WebkitBackgroundClip: 'text',
//               backgroundClip: 'text',
//               color: 'transparent', 
//             }}>assist</Box> you today
//         </Typography>
        
//         <Typography
//           variant="body1"
//           sx={{ fontFamily: "Gilroy-Regular", color: "#6B7280", mt: 1.5, zIndex: 1 }}
//         >
//           The right AI specialist for every task, just a chat away.
//         </Typography>
//       </Box>
//     );
//   }

//   return (
//     <Box
//       sx={{
//         flexGrow: 1, 
//         display: 'flex',
//         flexDirection: 'column',
//         gap: 3.5, 
//       }}
//     >
//       {messages.map((msg, index) => {
//         if (msg.sender === 'user') {
//           return (
//             <Box
//               key={msg.id}
//               sx={{
//                 display: 'flex',
//                 justifyContent: 'flex-end',
//                 alignItems: 'flex-start',
//                 gap: 1.5,
//               }}
//             >
//               <Paper
//                 elevation={0}
//                 sx={{
//                   p: '12px 16px',
//                   borderRadius: '12px',
//                   bgcolor: '#F3F4F6', 
//                   fontFamily: 'Gilroy-Regular',
//                   fontSize: '15px',
//                   color: '#1F2937',
//                   lineHeight: 1.6,
//                 }}
//               >
//                 {msg.text}
//               </Paper>
//               <Avatar sx={{ bgcolor: '#1F2937', width: 40, height: 40 }}>U</Avatar>
//             </Box>
//           );
//         }

//         if (msg.sender === 'ai') {
//           const isLastMessage = index === messages.length - 1;
//           return (
//             <Box key={msg.id}>
//               <Box
//                 sx={{
//                   display: 'flex',
//                   justifyContent: 'flex-start',
//                   alignItems: 'flex-start',
//                   gap: 1.5,
//                 }}
//               >
//                 <Avatar
//                     sx={{
//                         width: 40, 
//                         height: 40,
//                         background: 'linear-gradient(90deg, rgba(255, 64, 226, 0.3) 0%, rgba(0, 100, 255, 0.1) 42%, rgba(0, 100, 255, 0.3) 100%)',
//                     }}
//                 >
//                     <Box
//                         component="img"
//                         src="/assets/agent-image.svg"
//                         alt="Agent Icon"
//                         sx={{ width: 24, height: 24 }} 
//                     />
//                 </Avatar>

//                 <Box sx={{ flex: 1, width: 'calc(100% - 56px)' }}>
//                   <Typography sx={{ fontFamily: 'Gilroy-Bold', fontSize: '16px', color: '#1F2937', mb: 1 }}>
//                     {msg.agentName || 'Support Agent'}
//                   </Typography>

//                   <Box
//                     sx={{
//                       fontFamily: 'Gilroy-Regular',
//                       fontSize: '15px',
//                       color: '#1F2937',
//                       lineHeight: 1.7,
//                       '& p': { m: 0, mb: 1.5 },
//                       '& ol': { pl: 2.5, m: 0 },
//                       '& ul': { pl: 2.5, m: 0 },
//                       '& li': { mb: 1 },
//                       '& pre': { bgcolor: '#1F2937', color: '#fff', p: 1.5, borderRadius: 2, overflowX: 'auto' },
//                       '& code': { fontFamily: 'monospace' }
//                     }}
//                   >
//                     {/* Render message with Typewriter effect */}
//                     <TypewriterMarkdown text={msg.text} isTyping={msg.isTyping} />
//                   </Box>

//                   <Stack direction="row" spacing={0.5} sx={{ mt: 1.5 }}>
//                       <IconButton size="small" sx={{ color: '#6B7280' }}>
//                           <ThumbUpOutlinedIcon fontSize="small" />
//                       </IconButton>
//                       <IconButton size="small" sx={{ color: '#6B7280' }}>
//                           <ThumbDownOutlinedIcon fontSize="small" />
//                       </IconButton>
//                       <IconButton size="small" sx={{ color: '#6B7280' }}>
//                           <ContentCopyOutlinedIcon fontSize="small" />
//                       </IconButton>
//                   </Stack>
//                 </Box>
//               </Box>

//               {isLastMessage && !msg.isTyping && !isThinking && (
//                 <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
//                     <Button
//                         variant="outlined"
//                         startIcon={<AutorenewIcon />}
//                         sx={{
//                             color: '#101010',
//                             borderColor: '#101010', 
//                             textTransform: 'none',
//                             fontFamily: 'Gilroy-Medium',
//                             borderRadius: '30px', 
//                             '&:hover': {
//                                 bgcolor: '#F9FAFB', 
//                                 borderColor: '#101010', 
//                             }
//                         }}
//                     >
//                         Regenerate
//                     </Button>
//                 </Box>
//               )}
//             </Box>
//           );
//         }
//         return null;
//       })}

//       {/* --- RENDER THINKING INDICATOR --- */}
//       {isThinking && (
//           <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 1.5 }}>
//                <Avatar sx={{ width: 40, height: 40, background: 'linear-gradient(90deg, rgba(255, 64, 226, 0.3) 0%, rgba(0, 100, 255, 0.1) 42%, rgba(0, 100, 255, 0.3) 100%)' }}>
//                    <Box component="img" src="/assets/agent-image.svg" alt="Agent Icon" sx={{ width: 24, height: 24 }} />
//                </Avatar>
//                <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: '#F9FAFB', display: 'flex', alignItems: 'center', gap: 1 }}>
//                    <CircularProgress size={16} sx={{ color: '#19AF66' }} />
//                    <Typography sx={{ fontFamily: 'Gilroy-Medium', fontSize: '14px', color: '#6B7280' }}>
//                        Agent is analyzing your request...
//                    </Typography>
//                </Box>
//           </Box>
//       )}
//     </Box>
//   );
// };

// export default ChatWindow;